/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, type MemoExoticComponent, type ReactNode, useEffect, useMemo, useState } from 'react';

import AnalyticsWebClient, { originType, tenantType } from '@atlassiansox/analytics-web-client';
import { cssMap, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useThemeObserver } from '@atlaskit/tokens';
import type { AnalyticsAttributes } from '@atlassian/analytics-bridge';
import {
	PreviewPanel,
	PreviewPanelBody,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import { getAnalyticsEnv } from '../utils';

type Props = {
	analyticsAttributes?: AnalyticsAttributes;
	ari?: string;
	icon: ReactNode;
	iframeUrl: string;
	name: string;
	panelType?: string;
	parentProduct?: string;
	product: string;
	url: string;
};

const styles = cssMap({
	iframe: {
		width: '100%',
		height: '100%',
		border: 'none',
	},
	body: {
		display: 'flex',
		flexGrow: 1,
		overflowY: 'auto',
	},
});

export const getCloudIdFromAri = (ari?: string): string | undefined => {
	if (!ari || typeof ari !== 'string' || !ari.startsWith('ari:cloud:jira:')) {
		return undefined;
	}
	const parts = ari.split(':');

	if (parts.length !== 5) {
		return undefined;
	}

	const cloudId = parts[3];

	if (!cloudId) {
		return undefined;
	}

	return cloudId;
};

export const IFramePreviewPanel: MemoExoticComponent<({ url, iframeUrl, name, icon, panelType, product, parentProduct, ari, analyticsAttributes, }: Props) => JSX.Element> = memo(
	({
		url,
		iframeUrl,
		name,
		icon,
		panelType,
		product,
		parentProduct,
		ari,
		analyticsAttributes,
	}: Props): JSX.Element => {
		const { close } = usePreviewPanelsActions();
		const [analyticsClient, setAnalyticsClient] = useState<AnalyticsWebClient>();
		const cloudId = ari ?? getCloudIdFromAri(ari);
		const { colorMode } = useThemeObserver();

		useEffect(() => {
			setAnalyticsClient(() => {
				let analyticsClient = new AnalyticsWebClient({
					product,
					env: getAnalyticsEnv(window.location.hostname),
					origin: originType.WEB,
				});

				if (cloudId) {
					analyticsClient?.setTenantInfo(tenantType.CLOUD_ID, cloudId);
				}

				return analyticsClient;
			});
		}, [product, cloudId]);

		// Create iframe URL with theme parameter
		const iframeSrc = useMemo(() => {
			if (!iframeUrl || !colorMode) {
				return iframeUrl;
			}

			const url = new URL(iframeUrl, window.location.origin);
			url.searchParams.set('themeMode', colorMode);
			return url.toString();
		}, [iframeUrl, colorMode]);

		return (
			<PreviewPanel>
				<PreviewPanelHeader
					onClose={close}
					url={url}
					parentProduct={parentProduct}
					panelType={panelType}
					analyticsClient={analyticsClient}
				>
					<PreviewPanelTitle title={name} icon={icon} />
				</PreviewPanelHeader>
				<PreviewPanelBody
					styles={styles.body}
					parentProduct={parentProduct}
					panelType={panelType}
					analyticsClient={analyticsClient}
					{...(fg('add_capacity_tab_to_the_board_horizontal_nav') ? { analyticsAttributes } : {})}
				>
					<iframe src={iframeSrc} css={styles.iframe} title={name} />
				</PreviewPanelBody>
			</PreviewPanel>
		);
	},
);
